import { useCallback, useEffect, useMemo, useState } from "react"

import { useLazyGetSystemHealthTrendQuery } from "store/api/widgetsApi"

import { DEFAULT_PERIOD, TrendCard } from "./TrendCard"
import { computeTrend } from "./useTrendData"

export const SystemHealthCard = () => {
  const [period, setPeriod] = useState(DEFAULT_PERIOD)
  const [trigger, { data }] = useLazyGetSystemHealthTrendQuery()

  useEffect(() => {
    trigger({ period })
  }, [period, trigger])

  const alertsRaw = computeTrend(data?.data?.active_alerts, period)
  const invertedDirection =
    alertsRaw.trend.direction === "up" ? "down" : alertsRaw.trend.direction === "down" ? "up" : "flat"
  const alerts = {
    ...alertsRaw,
    trend: { ...alertsRaw.trend, direction: invertedDirection as "up" | "down" | "flat" },
    chart: alertsRaw.chart
      ? {
          ...alertsRaw.chart,
          color: invertedDirection === "down" ? "#EF4444" : invertedDirection === "up" ? "#22C55E" : "#94a3b8",
        }
      : alertsRaw.chart,
  }

  const perftest = useMemo(() => {
    const pt = data?.data?.perftest_data
    if (!pt || pt.last_period_total === 0) {
      return {
        value: "0%",
        valueExtra: "(Issues: 0%)",
        trend: { direction: "flat" as const, days: `past ${period} days` },
        lastPeriodCount: "0%",
        prevPeriodCount: "0%",
      }
    }

    const lastPassedPct = Math.round((pt.last_period_passed / pt.last_period_total) * 100)
    const prevPassedPct =
      pt.prev_period_total > 0 ? Math.round((pt.prev_period_passed / pt.prev_period_total) * 100) : 0
    const diff = lastPassedPct - prevPassedPct
    const direction: "up" | "down" | "flat" = diff > 0 ? "up" : diff < 0 ? "down" : "flat"
    const percent =
      prevPassedPct !== 0 ? Math.round(Math.abs(diff / prevPassedPct) * 100) : lastPassedPct !== 0 ? 100 : 0

    return {
      value: `${lastPassedPct}%`,
      valueExtra: `(Issues: ${100 - lastPassedPct}%)`,
      trend: {
        direction,
        ...(direction !== "flat" && { percent: `${percent}%` }),
        days: `past ${period} days`,
      },
      lastPeriodCount: `${lastPassedPct}%`,
      prevPeriodCount: `${prevPassedPct}%`,
    }
  }, [data?.data?.perftest_data, period])

  const handlePeriodChange = useCallback((newPeriod: number) => {
    setPeriod(newPeriod)
  }, [])

  return (
    <TrendCard
      title="System Health"
      onPeriodChange={handlePeriodChange}
      columns={[
        {
          label: "Active Alerts",
          value: alerts.value,
          tooltip: "Number of active alerts",
          tooltipPeriod: {
            period,
            lastPeriodCount: alerts.lastPeriodCount,
            prevPeriodCount: alerts.prevPeriodCount,
            unit: "alerts",
          },
          trend: alerts.trend,
          chart: alerts.chart,
        },
        {
          label: "Passed Tests",
          value: perftest.value,
          valueExtra: perftest.valueExtra,
          tooltip: "Percentage of tests that passed",
          tooltipPeriod: {
            period,
            lastPeriodCount: perftest.lastPeriodCount,
            prevPeriodCount: perftest.prevPeriodCount,
            unit: "passed",
          },
          trend: perftest.trend,
          chart: null,
        },
      ]}
    />
  )
}
